import { useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { useOnline } from '../../lib/use-online.ts';
import { useServerUserId } from '../../lib/server-session.ts';
import { useSyncStatus } from '../../services/sync.ts';
import { cx } from '../../ui/index.tsx';
import { CloudIcon, CloudOffIcon } from '../../ui/icons.tsx';
import { DAY_MS, timeAgo, useOldestPendingAge } from './data.ts';

/** Indicador de conexión y sincronización. Tocar = ver el estado completo (Sincronización). */
export function SyncBadge() {
  const { isOwner, business, session, membership } = useSession();
  const serverUser = useServerUserId();
  const noServerSession = session.mode === 'online' && serverUser !== undefined && serverUser !== membership.user_id;
  const online = useOnline();
  const status = useSyncStatus();
  const navigate = useNavigate();
  const pending = useLiveQuery(() => db.outbox.where('status').equals('pending').count(), [], 0);
  const rejected = useLiveQuery(() => db.outbox.where('status').equals('rejected').count(), [], 0);
  const oldestAge = useOldestPendingAge();
  // Dueño: cambios rechazados de otros dispositivos esperando que los revise.
  const toReview = useLiveQuery(
    async () => {
      if (!isOwner) return 0;
      const mine = new Set((await db.outbox.toArray()).map((o) => o.op_id));
      return db.sync_rejections
        .where('status')
        .equals('pending')
        .filter((r) => r.business_id === business.id && !mine.has(r.id))
        .count();
    },
    [isOwner, business.id],
    0,
  );
  const stale = oldestAge != null && oldestAge > DAY_MS;

  let label: string;
  let tone: string;
  if (!online) {
    label = pending ? `Sin conexión · ${pending} sin enviar` : 'Sin conexión';
    tone = stale ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
  } else if (noServerSession) {
    label = pending ? `Sin sesión · ${pending} sin enviar` : 'Sin sesión en el servidor';
    tone = 'bg-amber-100 text-amber-800';
  } else if (status.syncing) {
    label = 'Sincronizando…';
    tone = 'bg-sky-100 text-sky-800';
  } else if (rejected) {
    label = `${rejected} cambio${rejected === 1 ? '' : 's'} rechazado${rejected === 1 ? '' : 's'}`;
    tone = 'bg-red-100 text-red-800';
  } else if (status.lastError || pending) {
    label = pending ? `${pending} sin enviar` : 'Error al sincronizar';
    tone = stale ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
  } else if (toReview) {
    label = `${toReview} para revisar`;
    tone = 'bg-amber-100 text-amber-800';
  } else {
    label = status.lastSyncAt ? `Al día · ${timeAgo(status.lastSyncAt)}` : 'Conectado';
    tone = 'bg-emerald-100 text-emerald-800';
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/sincronizacion')}
      title={[status.lastError && `Último error: ${status.lastError}`, 'Ver el estado de la sincronización'].filter(Boolean).join('\n')}
      className={cx('inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium', tone)}
    >
      {online ? <CloudIcon className="size-4" /> : <CloudOffIcon className="size-4" />}
      <span className="max-w-[11rem] truncate">{label}</span>
    </button>
  );
}

/** Aviso arriba de todo si hay cambios de hace más de un día que no se pudieron enviar. */
export function StalePendingBanner() {
  const oldestAge = useOldestPendingAge();
  const navigate = useNavigate();
  if (oldestAge == null || oldestAge <= DAY_MS) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-900 ring-1 ring-red-200">
      <span>Hay cambios de hace más de un día sin enviar al servidor: hasta que se envíen, solo están en este dispositivo. Conectalo a internet.</span>
      <button type="button" className="font-semibold underline" onClick={() => navigate('/sincronizacion')}>
        Ver detalle
      </button>
    </div>
  );
}
